import { useEffect, useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScrollAndHighlight } from './useScrollAndHighlight';

interface UseTableRowNavigationOptions<T, TFilter = string, TView extends string = string> {
  /**
   * URL parameter name to watch (e.g., 'lead', 'project', 'paymentId')
   */
  paramName: string;

  /**
   * Data attribute name on table rows (e.g., 'data-lead-id', 'data-project-id')
   */
  dataAttribute: string;

  /**
   * Array of all items to search in
   */
  allItems: T[];

  /**
   * Function to get the ID from an item
   */
  getId: (item: T) => string;

  /**
   * Function to get the filter value from an item (e.g., status)
   */
  getFilterValue?: (item: T) => TFilter;

  /**
   * Current filter value
   */
  currentFilter?: TFilter;

  /**
   * Function to update the filter when item requires a different view
   */
  setFilter?: (value: TFilter) => void;

  /**
   * Optional: For view-based navigation (e.g., kanban vs archived)
   */
  viewConfig?: {
    currentView: TView;
    setView: (view: TView) => void;
    /** Check if item requires a specific view */
    getRequiredView: (item: T) => TView | null;
  };

  /**
   * Optional: Additional filters to clear when navigating
   */
  clearFilters?: () => void;

  /**
   * Optional: Async function to fetch item if not found in allItems
   */
  fetchItem?: (id: string) => Promise<T | null>;

  /**
   * Delay in ms before scrolling (default: 300ms, 400ms when filter changes)
   */
  scrollDelay?: number;
  scrollDelayOnFilterChange?: number;

  /**
   * Duration in ms to show the highlight ring (default: 1500ms)
   */
  highlightDuration?: number;
}

/**
 * Generic hook for navigating to a specific table row via URL parameters.
 * Handles filter adjustments, view switching, scroll-to-element, and highlight state.
 *
 * @returns highlightedItemId – ID of the item to highlight (from URL param). Pass to list/table
 *   and apply highlight classes when item.id === highlightedItemId. Cleared after highlightDuration.
 */
export function useTableRowNavigation<T, TFilter = string, TView extends string = string>({
  paramName,
  dataAttribute,
  allItems,
  getId,
  getFilterValue,
  currentFilter,
  setFilter,
  viewConfig,
  clearFilters,
  fetchItem,
  scrollDelay = 300,
  scrollDelayOnFilterChange = 400,
  highlightDuration = 1500,
}: UseTableRowNavigationOptions<T, TFilter, TView>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const { scrollToElement } = useScrollAndHighlight();

  const scrollToItem = useCallback(
    (id: string, delay: number) => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [
        setTimeout(() => scrollToElement(`[${dataAttribute}="${id}"]`), delay),
        setTimeout(() => setHighlightedItemId(null), delay + highlightDuration),
      ];
    },
    [dataAttribute, scrollToElement, highlightDuration]
  );

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    const itemId = searchParams.get(paramName);
    if (!itemId) return;

    // Set highlight immediately so it shows as soon as we have the param (survives re-renders)
    setHighlightedItemId(itemId);

    const handleNavigation = async () => {
      // First, try to find item in the provided array
      let item: T | undefined = allItems.find((i) => getId(i) === itemId);
      const foundInList = !!item;

      // If not found and we have a fetch function, try to load it
      if (!item && fetchItem) {
        const fetchedItem = await fetchItem(itemId);
        if (fetchedItem) {
          item = fetchedItem;
        }
      }

      if (!item) return;

      let needsFilterChange = false;
      let needsViewChange = false;

      // Handle view-based navigation (e.g., kanban vs archived)
      if (viewConfig) {
        const requiredView = viewConfig.getRequiredView(item);
        if (requiredView && viewConfig.currentView !== requiredView) {
          viewConfig.setView(requiredView);
          needsViewChange = true;
        }
      }

      // Handle filter-based navigation
      if (getFilterValue && setFilter) {
        const itemFilterValue = getFilterValue(item);
        if (currentFilter && currentFilter !== itemFilterValue) {
          setFilter(itemFilterValue);
          needsFilterChange = true;
        }
      }

      // Clear additional filters if needed
      if ((needsFilterChange || needsViewChange) && clearFilters) {
        clearFilters();
      }

      // Scroll to element with appropriate delay
      const delay = needsFilterChange || needsViewChange ? scrollDelayOnFilterChange : scrollDelay;
      scrollToItem(itemId, delay);

      // Only clear URL parameter when item was in the list (DOM is ready).
      // When item was fetched, keep param so effect runs again when list loads and we can scroll.
      if (foundInList) {
        setSearchParams({});
      }
    };

    // Only proceed if we have items to search or a fetch function
    if (allItems.length > 0 || fetchItem) {
      handleNavigation();
    }
  }, [
    searchParams,
    paramName,
    allItems,
    getId,
    getFilterValue,
    currentFilter,
    setFilter,
    viewConfig,
    clearFilters,
    fetchItem,
    scrollDelay,
    scrollDelayOnFilterChange,
    scrollToItem,
    setSearchParams,
  ]);

  return { highlightedItemId };
}
